import { useEffect, useState } from 'react';
import { getJwt, getSalesforceMessagingUrl } from '../../utils/miaw/chat-config';
import { IApiHooksWithTriggerData } from '../../interfaces/app-interfaces';
import { useNRLogging } from '../custom-hooks/useNRLogging';

interface MessageEntry {
  message: {
    id: string;
    fileId: string;
    text: string;
    inReplyToMessageId?: string;
  };
  esDeveloperName: string;
  isNewMessagingSession: boolean;
}
interface UploadParams {
  conversationId: string;
  messageEntry: MessageEntry;
  fileData: File;
}

interface IUploadRequestData {
  payload: UploadParams[];
  message?: string;
}

interface UploadResponse {
  data: unknown;
  status: number;
  fileName: string;
}

const useMIAWUploadFile = (): IApiHooksWithTriggerData<UploadResponse[], IUploadRequestData> => {
  const { logCustomAttributesNR } = useNRLogging();
  const messagingUrl = getSalesforceMessagingUrl();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [data, setData] = useState<UploadResponse[]>([]);
  const [requestData, setRequestData] = useState<IUploadRequestData>();

  const trigger = (requestData: IUploadRequestData) => {
    setRequestData(requestData);
  };

  useEffect(() => {
    const uploadFile = async () => {
      logCustomAttributesNR('ca-chatbot-miaw-api-request', {
        logLevel: 'info',
        message: 'Starting file upload',
        apiType: 'miawUploadFile',
      });
      setLoading(true);
      setError(undefined);
      setData([]);
      if (!Array.isArray(requestData?.payload) || !requestData.payload.length) {
        setError(new Error('Incorrect payload, please pass a non-empty array'));
        setLoading(false);
        throw new Error(`Incorrect payload, Please pass the correct payload`);
      }
      try {
        const UploadPromise = requestData.payload.map((payload) => {
          console.log(typeof payload, payload.messageEntry.message.text, 'payloadpayload');
          if (requestData?.message) {
            payload.messageEntry.message.text = requestData.message;
          }
          const formData = new FormData();
          const jsonBlob = new Blob([JSON.stringify(payload.messageEntry)], { type: 'application/json' });
          formData.append('messageEntry', jsonBlob);
          formData.append('fileData', payload.fileData, payload.fileData.name);
          return fetch(`${messagingUrl}/iamessage/api/v2/conversation/${payload.conversationId}/file`, {
            method: 'POST',
            body: formData,
            headers: {
              Authorization: 'Bearer ' + getJwt(),
            },
          });
        });

        const results = await Promise.allSettled(UploadPromise);
        console.log(results, 'resultsresults');

        results.forEach(async (result, index) => {
          const originalPayload = requestData.payload[index];
          if (result.status === 'fulfilled' && result.value) {
            const response = result.value;
            let parsedBody = null;
            try {
              parsedBody = await response.json();
            } catch (err) {
              setError(err.message);
            }
            setData((prev) => [...prev, { data: parsedBody, status: response.status, fileName: originalPayload.fileData.name }]);
          }
        });
        logCustomAttributesNR('ca-chatbot-miaw-api-response', {
          logLevel: 'info',
          message: 'File upload completed',
          apiType: 'miawUploadFile',
        });
      } catch (err) {
        if (err instanceof Error) {
          setError(new Error(err.message));
        } else {
          setError(new Error('Unexpected error occurred'));
        }
        logCustomAttributesNR('ca-chatbot-miaw-api-error', {
          errorName: err?.name,
          errorStack: err?.stack,
          message: err?.message,
          logLevel: 'error',
          apiType: 'miawUploadFile',
        });
      } finally {
        setLoading(false);
      }
    };
    if (requestData) {
      uploadFile();
    }
  }, [requestData]);

  return {
    data,
    loading,
    error,
    trigger,
  };
};

export { useMIAWUploadFile };
